<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  variant?:
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'transparent'
    | 'earn'
    | 'gold'
    | 'red'
    | 'rest'
    | 'payment'
    | 'payment-cancelled'
    | 'purchased'
    | 'gradient';
  gradientType?: 'horizontal' | 'vertical' | 'diagonal' | 'radial';
  gradientColors?: {
    from: string;
    to: string;
    via?: string;
  };
  hoverGradientColors?: {
    from: string;
    to: string;
    via?: string;
  };
  animationType?: 'shift' | 'brighten' | 'rotate' | 'pulse' | 'wave';
  isLoading?: boolean;
  height?: 'none' | 'md' | 'lg' | 'sm' | 'xl';
  width?: 'none' | 'md' | 'lg' | 'sm' | 'xs' | 'full';
  disabled?: boolean;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'circle';
  fontWeight?: 'semibold' | 'normal' | 'bold';
  fontSize?: string;
  type?: 'submit' | 'reset' | 'button';
  paddingText?: string;
  handleClick?: (event?: Event) => void;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  gradientType: 'horizontal',
  gradientColors: () => ({ from: '#1e40af', to: '#3b82f6' }),
  hoverGradientColors: () => ({ from: '#1d4ed8', to: '#60a5fa' }),
  animationType: 'shift',
  isLoading: false,
  height: 'md',
  width: 'md',
  disabled: false,
  rounded: 'lg',
  paddingText: 'px-4',
  fontWeight: 'semibold',
  fontSize: 'base',
  type: 'button',
  handleClick: () => {},
});

const generateGradient = (
  colors: { from: string; to: string; via?: string },
  type: string,
) => {
  const { from, to, via } = colors;

  switch (type) {
    case 'horizontal':
      return via
        ? `linear-gradient(to right, ${from}, ${via}, ${to})`
        : `linear-gradient(to right, ${from}, ${to})`;
    case 'vertical':
      return via
        ? `linear-gradient(to bottom, ${from}, ${via}, ${to})`
        : `linear-gradient(to bottom, ${from}, ${to})`;
    case 'diagonal':
      return via
        ? `linear-gradient(45deg, ${from}, ${via}, ${to})`
        : `linear-gradient(45deg, ${from}, ${to})`;
    case 'radial':
      return via
        ? `radial-gradient(circle, ${from}, ${via}, ${to})`
        : `radial-gradient(circle, ${from}, ${to})`;
    default:
      return `linear-gradient(to right, ${from}, ${to})`;
  }
};

// Computed property para generar el estilo de gradiente dinámico
const gradientStyle = computed((): Record<string, string> => {
  if (props.variant !== 'gradient') return {};

  const colors = props.gradientColors || { from: '#7827e6', to: '#431680' };
  const hoverColors = props.hoverGradientColors || {
    from: '#9c4dff',
    to: '#5a1a9b',
  };

  const normalGradient = generateGradient(colors, props.gradientType);
  const hoverGradient = generateGradient(hoverColors, props.gradientType);

  return {
    background: normalGradient,
    backgroundSize: '200% 200%',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    '--normal-gradient': normalGradient,
    '--hover-gradient': hoverGradient,
  };
});

// Computed property para las clases de animación
const animationClasses = computed(() => {
  if (props.variant !== 'gradient') return '';

  switch (props.animationType) {
    case 'shift':
      return 'gradient-shift';
    case 'brighten':
      return 'gradient-brighten';
    case 'rotate':
      return 'gradient-rotate';
    case 'pulse':
      return 'gradient-pulse';
    case 'wave':
      return 'gradient-wave';
    default:
      return 'gradient-shift';
  }
});

const roundedClasses = computed(() => {
  switch (props.rounded) {
    case 'none':
      return 'rounded-none';
    case 'sm':
      return 'rounded-sm';
    case 'md':
      return 'rounded-md';
    case 'lg':
      return 'rounded-lg';
    case 'xl':
      return 'rounded-xl';
    case 'full':
      return 'rounded-full';
    case 'circle':
      return '!rounded-full !h-10 !w-10 !px-0';
    default:
      return 'rounded-md';
  }
});
</script>

<template>
  <button
    :disabled="disabled"
    :style="variant === 'gradient' ? gradientStyle : {}"
    class="flex items-center justify-center transition-all duration-300 ease-out"
    :class="[
      roundedClasses,
      {
        'pointer-events-none': isLoading || disabled,
        'cursor-not-allowed': isLoading || disabled,
        'cursor-pointer': !isLoading && !disabled,
        'button-primary': variant == 'primary',
        'button-secondary': variant == 'secondary',
        'button-earn': variant == 'earn',
        'button-gold': variant == 'gold',
        'button-red': variant == 'red',
        'button-ghost': variant == 'ghost',
        'button-transparent': variant == 'transparent',
        'button-rest': variant == 'rest',
        'button-payment': variant == 'payment',
        'button-payment-cancelled': variant == 'payment-cancelled',
        'button-purchased': variant == 'purchased',
        'button-gradient': variant == 'gradient',
        [animationClasses]: variant == 'gradient',
        'h-auto py-0': height == 'none',
        'min-h-7 py-0.5': height == 'sm',
        'min-h-8 py-1': height == 'md',
        'min-h-10 py-2': height == 'lg',
        'min-h-12 py-3': height == 'xl',
        'px-0': width == 'none',
        'px-4': width == 'xs',
        'px-6': width == 'sm',
        'px-8': width == 'md',
        'px-12': width == 'lg',
        'w-full': width == 'full',
        'max-w-full': width !== 'full',
      },
    ]"
    @click="handleClick"
  >
    <div
      :class="`size-full flex items-center justify-center text-xs lg:text-base ${paddingText}`"
    >
      <span
        class="flex items-center"
        :class="{
          'opacity-0': isLoading,
          'font-semibold': fontWeight === 'semibold',
          'font-normal': fontWeight === 'normal',
          'font-bold': fontWeight === 'bold',
          'text-xs': fontSize === 'xs',
          'text-sm': fontSize === 'sm',
          'text-base': fontSize === 'base',
          'text-lg': fontSize === 'lg',
          'text-xl': fontSize === 'xl',
        }"
        :style="`font-size: ${fontSize}`"
      >
        <slot name="default" />
      </span>

      <IconifyIcon
        v-if="isLoading"
        height="1.6em"
        icon="mingcute:loading-line"
        class="absolute animate-spin"
      />
    </div>
  </button>
</template>

<style>
.button-gradient {
  position: relative;
  overflow: hidden;
  border: 0px solid transparent !important;
  color: white !important;
}

.button-gradient.gradient-shift:hover {
  background: var(--hover-gradient) !important;
  background-size: 200% 200% !important;
  animation: gradientShift 0.6s ease-in-out !important;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.button-gradient.gradient-brighten:hover {
  background: var(--hover-gradient) !important;
  filter: brightness(1.2) !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3) !important;
  transform: translateY(-2px) !important;
}

.button-gradient.gradient-rotate {
  background-size: 400% 400% !important;
}

.button-gradient.gradient-rotate:hover {
  background: var(--hover-gradient) !important;
  animation: gradientRotate 1s ease-in-out !important;
}

@keyframes gradientRotate {
  0% {
    background-position: 0% 0%;
  }
  25% {
    background-position: 100% 0%;
  }
  50% {
    background-position: 100% 100%;
  }
  75% {
    background-position: 0% 100%;
  }
  100% {
    background-position: 0% 0%;
  }
}

.button-gradient.gradient-pulse {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.button-gradient.gradient-pulse:hover {
  background: var(--hover-gradient) !important;
  transform: scale(1.05) !important;
  filter: brightness(1.3) !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4) !important;
}

.button-gradient.gradient-wave {
  background-size: 300% 300% !important;
}

.button-gradient.gradient-wave:hover {
  background: var(--hover-gradient) !important;
  animation: gradientWave 1.2s ease-in-out infinite !important;
}

@keyframes gradientWave {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.button-gradient::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s ease;
  z-index: 1;
}

.button-gradient:hover::before {
  left: 100%;
}

.button-gradient > div {
  position: relative;
  z-index: 2;
}

/* =========================
   PRIMARY
========================= */
/* .button-primary {
  background-color: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
}

.button-primary:hover {
  filter: brightness(1.15);
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
} */

/* =========================
   SECONDARY
========================= */

/* =========================
   GHOST
========================= */
.button-ghost {
  background: transparent;
  color: hsl(var(--foreground));
}

.button-ghost:hover {
  background-color: hsl(var(--secondary));
}

/* =========================
   TRANSPARENT
========================= */
.button-transparent {
  background: transparent;
  color: hsl(var(--muted-foreground));
}

.button-transparent:hover {
  color: hsl(var(--foreground));
}

/* =========================
   EARN (success / reward)
========================= */
.button-earn {
  background-color: hsl(var(--success));
  color: white;
}

.button-earn:hover {
  filter: brightness(1.15);
  transform: translateY(-1px);
}

.button-gold {
  background: linear-gradient(90deg, #a85a00 0%, #cf7408 100%);
  border: 1px solid #de8a21;
  color: #fff;
}

.button-gold:hover {
  filter: brightness(1.08);
  transform: none;
}

.button-red {
  background: #b91c1c;
  border: 1px solid #e81e1e;
  color: #fff;
  transition: all 0.3s ease;
}

.button-red:hover {
  filter: brightness(1.08);
}

/* =========================
   PAYMENT (Tournament Ticket)
========================= */
.button-payment {
  background: linear-gradient(90deg, #7b26d5 0%, #d526a3 100%);
  color: white;
  transition: all 0.3s ease;
}

.button-payment:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(213, 38, 163, 0.3);
}

.button-purchased {
  background: #298e4e8e; /* Tailwind green-700 */
  border: 1px solid #16a34a; /* Tailwind green-600 */
  color: #fff;
  transition: all 0.3s ease;
}

.button-purchased:hover {
  filter: brightness(1.1);
}

.button-payment-cancelled {
  background: #7f1d1d; /* Tailwind red-900 */
  border: 1px solid #b91c1c; /* Tailwind red-700 */
  color: #fff;
  transition: all 0.3s ease;
}

.button-payment-cancelled:hover {
  filter: brightness(1.1);
}

/* =========================
   REST / DISABLED LOOK
========================= */
.button-rest {
  background-color: hsl(var(--muted));
  color: hsl(var(--muted-foreground));
}

.button-rest:hover {
  background-color: hsl(var(--muted) / 0.9);
}
</style>
